import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Get saved language from localStorage or default to French
    return localStorage.getItem('language') || 'fr';
  });

  useEffect(() => {
    // Save language preference to localStorage
    localStorage.setItem('language', language);
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'fr' ? 'en' : 'fr');
  };

  const value = {
    language,
    setLanguage,
    toggleLanguage,
    t: (key) => translations[language][key] || key
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Translations object
const translations = {
  fr: {
    // Navigation
    nav_home: 'Accueil',
    nav_program: 'Programme',
    nav_gallery: 'Galerie',
    nav_location: 'Lieu',
    nav_contact: 'Contact',
    nav_reserve: 'Réserver',

    // Hero Section
    hero_presents: 'présente',
    hero_title_1: 'BATTLE OF THE YEAR',
    hero_title_2: 'NEXT GENERATION EUROPE',
    hero_dates: 'Samedi 7 novembre 2026',
    hero_location: 'Dijon',
    hero_description: 'Un jour de battles et célébration de la culture Hip-Hop. Rejoignez-nous pour un événement unique mêlant Breaking, échanges et compétition.',
    hero_cta_reserve: 'Réserver ma place',
    hero_cta_program: 'Voir le programme',

    // About Section
    about_title: 'À Propos de l\'Événement',
    about_subtitle: 'La Structure et HDMI Team vous invitent à vivre une journée intense dédiée à la culture Hip-Hop et au Breakdance.',
    about_feature1_title: 'Communauté',
    about_feature1_desc: 'Rassembler les passionnés de Hip-Hop et Breakdance de toute la région',
    about_feature2_title: 'Excellence',
    about_feature2_desc: 'Promouvoir le talent et le dépassement de soi à travers la compétition',
    about_feature3_title: 'Culture',
    about_feature3_desc: 'Célébrer et transmettre la culture Hip-Hop aux nouvelles générations',
    about_mission_title: 'Notre Mission',
    about_mission_text1: 'Créer un espace où les B-Boys et B-Girls peuvent s\'exprimer, apprendre et se surpasser dans un environnement positif et inspirant.',
    about_mission_text2: 'Du workshop à la compétition, en passant par des moments d\'échange et de partage, cet événement célèbre la richesse de la culture Hip-Hop et le talent de la nouvelle génération.',

    // Program Section
    program_title: 'Programme',
    program_subtitle: 'Une journée d\'événements passionnants vous attend',
    program_day_friday: 'Vendredi',
    program_day_saturday: 'Samedi',
    program_day_sunday: 'Dimanche',
    program_date_friday: '7 Novembre 2026',
    program_date_saturday: '7 Novembre 2026',
    program_date_sunday: '7 Novembre 2026',
    
    // Friday Events
    program_fri_inauguration: 'Inauguration',
    program_fri_inauguration_desc: 'Lancement officiel de l\'événement. Un moment convivial autour d\'un buffet sera proposé',
    program_fri_film: 'Projection Film',
    program_fri_film_desc: 'Diffusion du documentaire "Planet B-Boy"',
    program_fri_debate: 'Échange & Débat',
    program_fri_debate_desc: 'Temps d\'échange et de débat avec les participants',

    // Saturday Events
    program_sat_form_button: '📝 Formulaire d\'inscription',
    program_sat_buy_button: '🎟️ Acheter vos places',
    program_sat_workshop1: 'Workshop Sarah Bee',
    program_sat_workshop1_desc: 'Stage d\'apprentissage avec Sarah Bee',
    program_sat_workshop2: 'Workshop Poe One',
    program_sat_workshop2_desc: 'Stage d\'apprentissage avec Poe One',
    program_sat_workshop3: 'Workshop Niek',
    program_sat_workshop3_desc: 'Stage d\'apprentissage avec Niek',
    program_sat_roundtable: 'Table Ronde Fédération',
    program_sat_roundtable_desc: 'Rencontre avec la Fédération',
    program_sat_wings: 'Wing Workshop',
    program_sat_wings_desc: 'Workshop wing',
    program_sat_warmup: 'Warm Up / Open Practice',
    program_sat_warmup_desc: 'Échauffement et entraînement libre',
    program_sat_party: 'Soirée Jam Breaking',
    program_sat_party_desc: 'Soirée avant le battle du dimanche',

    // Sunday Events
    program_sun_boty: 'BOTY Next Generation',
    program_sun_boty_desc: 'Battle of the Year - Compétition officielle',

    // Badges
    badge_free: 'GRATUIT',
    badge_paid: 'PAYANT',
    button_reserve_stage: 'Réserver ce stage',

    // Gallery Section
    gallery_title: 'Galerie',
    gallery_subtitle: 'Revivez les moments forts de nos précédents événements',
    gallery_follow: 'Suivez-nous sur les réseaux sociaux pour plus de photos et vidéos',

    // Location Section
    location_title: 'Lieux de l\'Événement',
    location_subtitle: 'Un lieu d\'exception à Dijon pour 1 jour d\'événement',
    location_gym_boivin: 'Gymnase Jean Marion',
    location_gym_sellenet: 'Gymnase André Sellenet',
    location_days_fri_sat: 'Samedi 7 Novembre 2026',
    location_day_sun: 'Samedi 7 Novembre 2026',
    location_address: 'Adresse',
    location_access: 'Accès',
    location_tram: 'En tramway:',
    location_bus: 'En bus:',
    location_car: 'En voiture:',
    location_parking: 'Parking disponible à proximité',
    location_get_directions: 'Obtenir l\'itinéraire',
    location_hours_title: 'Horaires Généraux',
    location_hours_friday: 'Samedi 7 Novembre 2026',
    location_hours_saturday: 'Samedi 7 Novembre 2026',
    location_hours_sunday: 'Samedi 7 Novembre 2026',

    // Partners Section
    partners_title: 'Nos Partenaires',
    partners_subtitle: 'Ils soutiennent notre événement',
    partners_become_title: 'Vous souhaitez devenir partenaire ?',
    partners_become_desc: 'Rejoignez-nous pour soutenir la culture Hip-Hop et les jeunes talents. Contactez-nous pour découvrir nos offres de partenariat.',
    partners_contact_btn: 'Nous contacter',

    // Contact Section
    contact_title: 'Contact & Réseaux',
    contact_subtitle: 'Restez connectés avec nous',
    contact_email: 'Email',
    contact_instagram: 'Instagram',
    contact_tiktok: 'TikTok',
    contact_ready_title: 'Prêt à vivre l\'expérience ?',
    contact_ready_desc: 'Ne manquez pas cet événement unique ! Réservez votre place dès maintenant et rejoignez-nous pour 3 jours inoubliables.',
    contact_reserve_now: 'Réserver maintenant',
    contact_follow: 'Suivez-nous pour ne rien manquer',

    // Footer
    footer_about_desc: 'Association dédiée à la promotion de la culture Hip-Hop et du Breakdance.',
    footer_quick_links: 'Liens Rapides',
    footer_contact: 'Contact',
    footer_copyright: 'La Structure / HDMI Team. Tous droits réservés.',
    footer_made_with: 'Fait avec',
    footer_for_culture: 'pour la culture Hip-Hop',
  },
  
  en: {
    // Navigation
    nav_home: 'Home',
    nav_program: 'Program',
    nav_gallery: 'Gallery',
    nav_location: 'Location',
    nav_contact: 'Contact',
    nav_reserve: 'Reserve',

    // Hero Section
    hero_presents: 'presents',
    hero_title_1: 'BATTLE OF THE YEAR',
    hero_title_2: 'NEXT GENERATION EUROPE',
    hero_dates: 'Saturday 7 November 2026',
    hero_location: 'Dijon',
    hero_description: 'A day of battles and celebrating hip-hop culture. Join us for a one-of-a-kind event bringing together breaking, connection, and competition.',
    hero_cta_reserve: 'Get your ticket',
    hero_cta_program: 'View the program',

    // About Section
    about_title: 'About the Event',
    about_subtitle: 'La Structure and HDMI Team invite you to experience an intense day dedicated to hip-hop culture and breaking.',
    about_feature1_title: 'Community',
    about_feature1_desc: 'Bringing together people into hip-hop and breaking from all over',
    about_feature2_title: 'Excellence',
    about_feature2_desc: 'Creating a space where talent can shine and push beyond limits through competition',
    about_feature3_title: 'Culture',
    about_feature3_desc: 'Celebrating and passing on hip-hop culture to the next generation',
    about_mission_title: 'Our Mission',
    about_mission_text1: 'To create a space where B-Boys and B-Girls can express themselves, learn, and push their limits in a positive and inspiring environment.',
    about_mission_text2: 'From workshops to battles, along with moments to connect and share, this event highlights the richness of hip-hop culture and the talent of the next generation.',

    // Program Section
    program_title: 'Program',
    program_subtitle: 'An exciting day of events awaits',
    program_day_friday: 'Friday',
    program_day_saturday: 'Saturday',
    program_day_sunday: 'Sunday',
    program_date_friday: '7 November 2026',
    program_date_saturday: '7 November 2026',
    program_date_sunday: '7 November 2026',
    
    // Friday Events
    program_fri_inauguration: 'Opening Ceremony',
    program_fri_inauguration_desc: 'Official launch of the event. A friendly moment around a buffet will be offered',
    program_fri_film: 'Film Screening',
    program_fri_film_desc: 'Screening of the documentary "Planet B-Boy"',
    program_fri_debate: 'Talk & Discussion',
    program_fri_debate_desc: 'Exchange and discussion time with participants',

    // Saturday Events
    program_sat_form_button: '📝 Registration Form',
    program_sat_buy_button: '🎟️ Buy Tickets',
    program_sat_workshop1: 'Workshop Sarah Bee',
    program_sat_workshop1_desc: 'Training sessions with Sarah Bee',
    program_sat_workshop2: 'Workshop Poe One',
    program_sat_workshop2_desc: 'Training sessions with Poe One',
    program_sat_workshop3: 'Workshop Niek',
    program_sat_workshop3_desc: 'Training sessions with Niek',
    program_sat_roundtable: 'Federation Roundtable',
    program_sat_roundtable_desc: 'Meet and exchange with the federation',
    program_sat_wings: 'Wing Workshop',
    program_sat_wings_desc: 'Workshop wing',
    program_sat_warmup: 'Warm Up / Open Practice',
    program_sat_warmup_desc: 'Warm-up and open training',
    program_sat_party: 'DJAM Breaking Night',
    program_sat_party_desc: 'Pre-battle party before Sunday\'s competition',

    // Sunday Events
    program_sun_boty: 'BOTY Next Generation',
    program_sun_boty_desc: 'Official Battle of the Year competition',

    // Badges
    badge_free: 'FREE',
    badge_paid: 'PAID',
    button_reserve_stage: 'Book this workshop',

    // Gallery Section
    gallery_title: 'Gallery',
    gallery_subtitle: 'Relive highlights from previous events',
    gallery_follow: 'Follow us on social media for more photos and videos',
    gallery_instagram: 'Instagram',
    gallery_tiktok: 'TikTok',

    // Location Section
    location_title: 'Event Locations',
    location_subtitle: 'One exceptional venue in Dijon for one day of events',
    location_gym_boivin: 'Gymnase Jean Marion',
    location_gym_sellenet: 'Gymnase André Sellenet',
    location_days_fri_sat: 'Saturday 7 November 2026',
    location_day_sun: 'Saturday 7 November 2026',
    location_address: 'Address',
    location_access: 'Access',
    location_tram: 'By tram:',
    location_bus: 'By bus:',
    location_car: 'By car:',
    location_parking: 'Parking available nearby',
    location_get_directions: 'Get directions',
    location_hours_title: 'General Schedule',
    location_hours_friday: 'Saturday, 7 November 2026',
    location_hours_saturday: 'Saturday, 7 November 2026',
    location_hours_sunday: 'Saturday, 7 November 2026',

    // Partners Section
    partners_title: 'Our Partners',
    partners_subtitle: 'They support the event',
    partners_become_title: 'Want to become a partner?',
    partners_become_desc: 'Join us to support hip-hop culture and young talents. Contact us to discover our partnership offers.',
    partners_contact_btn: 'Contact us',

    // Contact Section
    contact_title: 'Contact & Socials',
    contact_subtitle: 'Stay connected with us',
    contact_email: 'Email',
    contact_instagram: 'Instagram',
    contact_tiktok: 'TikTok',
    contact_ready_title: 'Ready to be part of it?',
    contact_ready_desc: 'Don\'t miss out — grab your ticket now and join us for three unforgettable days.',
    contact_reserve_now: 'Book now',
    contact_follow: 'Follow us to stay updated',

    // Footer
    footer_about_desc: 'Association dedicated to promoting hip-hop culture and breaking',
    footer_quick_links: 'Quick Links',
    footer_contact: 'Contact',
    footer_copyright: 'La Structure / HDMI Team. All rights reserved.',
    footer_made_with: 'Made with',
    footer_for_culture: 'for hip-hop culture',
  }
};

export default translations;
